import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Button, Chip, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';

import { reportingUsageAPI } from '../services/reportingUsageApi';
import { saveBlob, downloadError } from '../utils/download';
import ReportingAllocations from '../components/ReportingAllocations';
import ReportingCustomerInfra from '../components/ReportingCustomerInfra';
import {
  PeriodPicker, Stat, StatGrid, Legend, BarChart, CustomerPicker, useCustomers, PALETTE, PERIODS, num, errText, monthShort, monthLong, typeLabel,
} from '../components/ReportingUsageParts';

const NO_PRINT = { '@media print': { display: 'none' } };
const TYPE_COLOR = { P: PALETTE[0], B: PALETTE[1], L: PALETTE[2], Unknown: '#757575' };
const FILE_PERIOD = { last_month: 'Last_Month', last_year: 'Last_12_Months', all_time: 'All_Time' };
const USAGE_KEYS = [{ key: 'samples', label: 'Samples', color: PALETTE[0] }, { key: 'tests', label: 'Tests', color: PALETTE[1] }];

// One customer's profile: usage, sessions, database size, volumes, AWS cost and licence, with CSV exports and a print view.
// Props: custCode (optional, opens that customer), onSelectCustomer(code) (optional, called when the picker changes so the page can keep its address in step).
export default function ReportingCustomerOverview({ custCode = '', onSelectCustomer }) {
  const [activeOnly, setActiveOnly] = useState(true);
  const { customers, error: listError } = useCustomers(activeOnly);
  const [selected, setSelected] = useState(custCode);
  const [dimension, setDimension] = useState('last_year');
  const [exportPeriod, setExportPeriod] = useState('last_year');
  const [info, setInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setSelected(custCode || ''); }, [custCode]);

  useEffect(() => {
    if (!selected) { setInfo(null); setUsage(null); setSessions(null); return undefined; }
    let live = true;
    setLoading(true); setError('');
    Promise.all([reportingUsageAPI.customer(selected), reportingUsageAPI.customerUsage(selected, dimension), reportingUsageAPI.customerSessions(selected, dimension)])
      .then(([c, u, s]) => { if (live) { setInfo(c.data); setUsage(u.data); setSessions(s.data); } })
      .catch((err) => { if (live) { setInfo(null); setUsage(null); setSessions(null); setError(errText(err, 'Could not load the customer')); } })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [selected, dimension]);

  const choose = (code) => { setSelected(code); if (onSelectCustomer) onSelectCustomer(code); };
  const download = async (call, filename) => {
    try { const res = await call(); saveBlob(new Blob([res.data], { type: 'text/csv' }), filename); }
    catch (err) { setError(await downloadError(err, 'CSV export failed')); }
  };

  const months = {};
  (usage?.rows || []).forEach((r) => {
    const m = months[r.log_month] || (months[r.log_month] = { label: monthShort(r.log_month), values: { samples: 0, tests: 0 } });
    m.values.samples += Number(r.order_count || 0); m.values.tests += Number(r.ord_task_count || 0);
  });
  const usageChart = Object.keys(months).sort().map((k) => months[k]);
  const sMonths = sessions?.months || [];
  const types = [...new Set(sMonths.flatMap((m) => Object.keys(m.by_type)))];
  const typeKeys = types.map((t, i) => ({ key: t, label: typeLabel(t), color: TYPE_COLOR[t] || PALETTE[(i + 3) % PALETTE.length] }));
  const sessionChart = sMonths.map((m) => ({ label: monthShort(m.log_month), values: m.by_type }));

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Customer Overview</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ...NO_PRINT }}>
        Samples, tests, sessions and licence for one customer. Periods are whole months and end before the current month.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2, ...NO_PRINT }}>
        <CustomerPicker customers={customers} value={selected} onChange={choose} activeOnly={activeOnly} onActiveOnly={setActiveOnly} />
        <PeriodPicker value={dimension} onChange={setDimension} />
        {info && !loading && (
          <>
            <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Print</Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => download(() => reportingUsageAPI.exportCustomerCsv(selected), `${selected.toUpperCase()}_profile_export.csv`)}>Export sample and test counts</Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <TextField select size="small" label="Export period" value={exportPeriod} onChange={(e) => setExportPeriod(e.target.value)} sx={{ minWidth: 150 }}>
          {Object.entries(PERIODS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
        </TextField>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
          onClick={() => download(() => reportingUsageAPI.exportAllCsv(exportPeriod), `All_Customers_Sample_Test_Counts_${FILE_PERIOD[exportPeriod]}.csv`)}>Export all customers</Button>
      </Box>
      {(error || listError) && <Alert severity="error" sx={{ mb: 2 }}>{error || listError}</Alert>}
      {!selected && !error && <Typography color="text.secondary">Select a customer to see their profile.</Typography>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {info && usage && sessions && !loading && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6">{info.cust_name}</Typography>
              <Typography variant="body2" color="text.secondary">{[info.cust_code, info.cl_number, info.location, PERIODS[dimension]].filter(Boolean).join('  |  ')}</Typography>
            </Box>
            <Chip color={info.is_active ? 'success' : 'default'} label={info.is_active ? 'Active' : 'Inactive'} />
          </Paper>
          <StatGrid>
            <Stat label="Total samples" value={num(usage.total_orders)} sub={PERIODS[dimension]} />
            <Stat label="Total tests" value={num(usage.total_tasks)} sub={PERIODS[dimension]} />
            <Stat label="Total sessions" value={num(sessions.total)} sub={PERIODS[dimension]} />
            <Stat label="Months of data" value={num(usage.month_count)} sub={usage.date_from ? `${usage.date_from} to ${usage.date_to}` : 'all time'} />
          </StatGrid>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2, minWidth: 0 }}>
              <Typography variant="subtitle1" gutterBottom>Samples and tests</Typography>
              <Legend items={USAGE_KEYS} />
              {usageChart.length ? <BarChart data={usageChart} keys={USAGE_KEYS} ariaLabel="Samples and tests per month" /> : <Typography color="text.secondary">No usage in this period.</Typography>}
            </Paper>
            <Paper sx={{ p: 2, minWidth: 0 }}>
              <Typography variant="subtitle1" gutterBottom>Sessions by type</Typography>
              <Legend items={typeKeys} />
              {sessionChart.length ? <BarChart data={sessionChart} keys={typeKeys} stacked ariaLabel="Sessions per month by user type" /> : <Typography color="text.secondary">No session data.</Typography>}
            </Paper>
          </Box>
          {sMonths.length > 0 && (
            <Paper sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Session summary by month</Typography>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader aria-label="Session summary by month">
                  <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Total</TableCell>{types.map((t) => <TableCell key={t} align="right">{typeLabel(t)}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {[...sMonths].reverse().map((m) => (
                      <TableRow key={m.log_month} hover>
                        <TableCell>{monthLong(m.log_month)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>{num(m.total)}</TableCell>
                        {types.map((t) => <TableCell key={t} align="right">{num(m.by_type[t])}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          <ReportingCustomerInfra custCode={info.cust_code} dimension={dimension} />
          <Typography variant="subtitle1" gutterBottom>Licence</Typography>
          <ReportingAllocations custCode={info.cust_code} canEdit={false} />
        </>
      )}
    </Box>
  );
}
